import { makeModuleApiInfoSelector, makeModuleI18nSelector } from "@client/content/selectors";
import { apiAction } from "@client/lib/actions";
import { parseQueryString } from "@client/lib/api";
import { goTo } from "@client/lib/navigation";
import { type AppState, type BaseAppState, type EntityRef, Status } from "@client/types";
import appInfo from "@common/appInfo";
import toasts from "@common/lib/toast";
import { type Dispatch } from "redux";
import {
  type CloseTicketBeginActionData,
  type CloseTicketSuccessActionData,
  type CreateCommentBeginActionData,
  type CreateCommentSuccessActionData,
  type RateBeginActionData,
  type RateSuccessActionData
} from "../MemberHelpdesk/actions";
import apiHelpdesk from "../MemberHelpdesk/api";
import { type MemberHelpdesk } from "../MemberHelpdesk/types";
import { initAction } from "../actions";
import api from "./api";
import { persist } from "./lib";
import { localStateSelector } from "./selectors";
import { type TicketDetail } from "./types";

export enum Actions {
  InitBegin = "TICKET_DETAIL_INIT_BEGIN",
  InitFailure = "TICKET_DETAIL_INIT_FAILURE",
  InitSuccess = "TICKET_DETAIL_INIT_SUCCESS",

  CreateCommentBegin = "TICKET_DETAIL_CREATE_COMMENT_BEGIN",
  CreateCommentFailure = "TICKET_DETAIL_CREATE_COMMENT_FAILURE",
  CreateCommentSuccess = "TICKET_DETAIL_CREATE_COMMENT_SUCCESS",

  CloseTicketBegin = "TICKET_DETAIL_CLOSE_TICKET_BEGIN",
  CloseTicketFailure = "TICKET_DETAIL_CLOSE_TICKET_FAILURE",
  CloseTicketSuccess = "TICKET_DETAIL_CLOSE_TICKET_SUCCESS",

  CreateUserFeedbackBegin = "TICKET_DETAIL_CREATE_USER_FEEDBACK_BEGIN",
  CreateUserFeedbackFailure = "TICKET_DETAIL_CREATE_USER_FEEDBACK_FAILURE",
  CreateUserFeedbackSuccess = "TICKET_DETAIL_CREATE_USER_FEEDBACK_SUCCESS",

  GetTicketDataBegin = "TICKET_DETAIL_GET_TICKET_DATA_BEGIN",
  GetTicketDataFailure = "TICKET_DETAIL_GET_TICKET_DATA__FAILURE",
  GetTicketDataSuccess = "TICKET_DETAIL_GET_TICKET_DATA_SUCCESS"
}

export type InitBeginActionData = EntityRef & TicketDetail.Api.InitRequest;
export type InitSuccessActionData = EntityRef & TicketDetail.Api.InitResponse;

export type InitArgs = EntityRef & {};
export const init = (args: InitArgs) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const state = getState();
  const i18n = makeModuleI18nSelector()(state, args);
  const localState = localStateSelector(state, args);
  const ticketId = parseQueryString().t;
  const scheduleId = parseQueryString().s;
  const persistState = persist.get(state.portal.id, appInfo.user?.id ?? null);
  if (
    persistState &&
    persistState.email &&
    ticketId === persistState.ticketId &&
    scheduleId === persistState.scheduleId
  ) {
    dispatch(getTicketData(args, persistState.email));
    return;
  }

  if (ticketId && scheduleId) {
    dispatch(_init({ ...args, ticketId: ticketId, scheduleId: scheduleId }));
  } else if (localState.status === Status.None) {
    dispatch({
      type: Actions.InitSuccess,
      data: <InitSuccessActionData>{
        id: args.id,
        obfuscatedEmail: null,
        error: ticketId ? i18n.noTicketAccessError : i18n.noTicketIdError
      }
    });
  }
};

export const _init = initAction<
  BaseAppState,
  InitBeginActionData,
  TicketDetail.Api.InitResponse,
  InitSuccessActionData
>({
  types: {
    begin: Actions.InitBegin,
    failure: Actions.InitFailure,
    success: Actions.InitSuccess
  },
  action: (state: BaseAppState, dispatch: any, params: InitBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.init(apiInfo, params);
  }
});

export const createComment =
  (ref: EntityRef, data: MemberHelpdesk.View.NewComment) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();

    const localState = localStateSelector(state, ref);

    if (localState.status !== "Commenting") {
      dispatch(_createComment({ ...ref, data, ticketId: localState.detail?.ticketDetail.ticketId! }));
    }
  };

export const _createComment = apiAction<
  BaseAppState,
  CreateCommentBeginActionData,
  MemberHelpdesk.Api.CreateCommentResponse,
  CreateCommentSuccessActionData
>({
  types: {
    begin: Actions.CreateCommentBegin,
    failure: Actions.CreateCommentFailure,
    success: [
      Actions.CreateCommentSuccess,
      () => {
        toasts.saved();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: CreateCommentBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    const localState = localStateSelector(state, params);
    return apiHelpdesk.createComment(apiInfo, {
      ...params.data,
      ticketId: localState.detail?.ticketDetail.ticketId!
    });
  }
});

export const closeTicket = (ref: EntityRef) => (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
  const state = getState();
  const localState = localStateSelector(state, ref);

  if (localState.status !== "Commenting") {
    dispatch(_closeTicket({ ...ref, ticketId: localState.detail?.ticketDetail.ticketId! }));
  }
};

export const _closeTicket = apiAction<
  BaseAppState,
  CloseTicketBeginActionData,
  MemberHelpdesk.Api.CloseTicketResponse,
  CloseTicketSuccessActionData
>({
  types: {
    begin: Actions.CloseTicketBegin,
    failure: Actions.CloseTicketFailure,
    success: [
      Actions.CloseTicketSuccess,
      () => {
        toasts.saved();
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: CloseTicketBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    const localState = localStateSelector(state, params);

    return apiHelpdesk.closeTicket(apiInfo, {
      ticketId: localState.detail?.ticketDetail.ticketId!
    });
  }
});

export const rate =
  (ref: EntityRef, rating: number, ticketFeedbackQuestionId: string, permalink: string | null) =>
  (dispatch: Dispatch<any>, getState: () => BaseAppState) => {
    const state = getState();
    const localState = localStateSelector(state, ref);

    if (localState.status !== "Commenting") {
      dispatch(
        _rate({
          ...ref,
          ticketId: localState.detail?.ticketDetail.ticketId!,
          rating,
          ticketFeedbackQuestionId,
          permalink: permalink
        })
      );
    }
  };

export const _rate = apiAction<
  BaseAppState,
  RateBeginActionData,
  MemberHelpdesk.Api.CreateUserFeedbackResponse,
  RateSuccessActionData
>({
  types: {
    begin: Actions.CreateUserFeedbackBegin,
    failure: Actions.CreateUserFeedbackFailure,
    success: [
      Actions.CreateUserFeedbackSuccess,
      ({ params }) => {
        toasts.saved();
        params.permalink && goTo(params.permalink);
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: RateBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    const localState = localStateSelector(state, params);

    return apiHelpdesk.createUserFeedback(apiInfo, {
      ticketId: localState.detail?.ticketDetail.ticketId!,
      rating: params.rating,
      ticketFeedbackQuestionId: params.ticketFeedbackQuestionId
    });
  }
});

export type GetTicketDataBeginActionData = EntityRef & TicketDetail.Api.GetTicketDataRequest;
export type GetTicketDataSuccessActionData = EntityRef & TicketDetail.Api.GetTicketDataResponse;

export const getTicketData = (ref: EntityRef, email: string) => (dispatch: Dispatch<any>, getState: () => AppState) => {
  const ticketId = parseQueryString().t;
  const scheduleId = parseQueryString().s;
  dispatch(_getTicketData({ ...ref, ticketId, scheduleId, email }));
};

export const _getTicketData = apiAction<
  AppState,
  GetTicketDataBeginActionData,
  TicketDetail.Api.GetTicketDataResponse,
  GetTicketDataSuccessActionData
>({
  types: {
    begin: Actions.GetTicketDataBegin,
    failure: Actions.GetTicketDataFailure,
    success: [
      Actions.GetTicketDataSuccess,
      ({ getState, params }) => {
        const state = getState();
        persist.set(state.portal.id, appInfo.user?.id ?? null, {
          email: params.email,
          scheduleId: params.scheduleId,
          ticketId: params.ticketId
        });
      }
    ]
  },
  action: (state: BaseAppState, dispatch: any, params: GetTicketDataBeginActionData) => {
    const apiInfo = makeModuleApiInfoSelector()(state, params);
    return api.getTicketData(apiInfo, params);
  }
});
